import { useSnapshot } from 'valtio'
import IconBack from '@/components/Icons/Back'
import { Link } from '@/lib/navigation'
import { state, useRadarNav } from '@/lib/state'
import { cn } from '@/lib/utils'
import styles from './Logo.module.css'

export function Logo() {
  const { radarConfig } = useSnapshot(state)
  const { isHomePage } = useRadarNav()

  return (
    <Link
      href=""
      className={cn(styles.logo, !isHomePage && styles.small)}
      data-testid="logo-container"
    >
      {!isHomePage && (
        <IconBack className={styles.back} data-testid="logo-back-icon" />
      )}
      {radarConfig?.logoSvg ? (
        <div
          className={styles.src}
          // Using dangerouslySetInnerHTML is necessary for SVG rendering
          // biome-ignore lint/security/noDangerouslySetInnerHtml: The content is validated before being stored in the radarConfig
          dangerouslySetInnerHTML={{ __html: radarConfig.logoSvg }}
          aria-label="Tech Radar Logo"
          role="img"
          data-testid="logo-svg"
        />
      ) : (
        <div className={styles.src} data-testid="logo-svg">
          {/* Fallback if no SVG is defined in config */}
          <svg viewBox="0 0 149.6 64.73" aria-labelledby="logoTitle">
            <title id="logoTitle">Tech Radar</title>
            <path
              d="M25.87,0h-1.77v7.24C10.65,8.15,0,19.35,0,33.02s11.62,25.87,25.87,25.87,24.87-10.65,25.79-24.1h7.24v-1.77C58.89,14.81,44.09,0,25.87,0ZM25.87,55.32c-12.31,0-22.32-10.01-22.32-22.32S12.59,11.67,24.07,10.76v24.01h24.01c-.92,11.48-10.54,20.52-22.24,20.52h0l.03.03ZM51.74,31.22h-24.1V3.63c14.81.89,26.7,12.78,27.59,27.62h-3.52l.03-.03Z"
              fill="currentColor"
              fillRule="evenodd"
              strokeWidth="0"
            />
            <path
              d="M78.09,10.41h-6.09v16.5h-1.5V10.41h-6.08v-1.27h13.67v1.27Z"
              fill="currentColor"
              strokeWidth="0"
            />
            <path
              d="M90.78,18.39h-8.31v7.25h9.56v1.27h-11.06V9.14h11v1.27h-9.5v6.71h8.31v1.27Z"
              fill="currentColor"
              strokeWidth="0"
            />
            <path
              d="M107.77,21.37c-.2,1.87-.87,3.3-2.01,4.3-1.13.99-2.65,1.49-4.53,1.49-1.32,0-2.48-.33-3.5-.99-1.01-.66-1.8-1.59-2.35-2.8-.55-1.21-.83-2.59-.84-4.14v-2.31c0-1.58.28-2.98.83-4.2.55-1.22,1.35-2.16,2.39-2.83,1.04-.66,2.23-1,3.58-1,1.9,0,3.41.51,4.51,1.54,1.1,1.03,1.74,2.45,1.92,4.26h-1.51c-.38-3.02-2.01-4.53-4.92-4.53-1.61,0-2.9.6-3.85,1.81s-1.43,2.87-1.43,5v2.17c0,2.05.46,3.69,1.4,4.91.93,1.22,2.19,1.83,3.78,1.83s2.75-.38,3.55-1.13c.8-.75,1.29-1.88,1.48-3.39h1.51Z"
              fill="currentColor"
              strokeWidth="0"
            />
            <path
              d="M124.59,26.91h-1.51v-8.52h-10.16v8.52h-1.5V9.14h1.5v7.98h10.16v-7.98h1.51v17.77Z"
              fill="currentColor"
              strokeWidth="0"
            />
            <path
              d="M71.73,41.62h-2.32v6.29h-4.28v-17.77h6.99c2.11,0,3.76.47,4.94,1.4,1.19.93,1.78,2.26,1.78,3.96,0,1.24-.25,2.26-.75,3.07-.5.81-1.28,1.47-2.35,1.98l3.71,7.18v.18h-4.59l-3.14-6.29ZM69.41,38.33h2.71c.81,0,1.43-.21,1.84-.64.41-.43.62-1.03.62-1.79s-.21-1.37-.62-1.81-1.03-.65-1.83-.65h-2.71v4.9Z"
              fill="currentColor"
              strokeWidth="0"
            />
            <path
              d="M91.53,44.59h-5.87l-1.03,3.32h-4.58l6.52-17.77h4.03l6.57,17.77h-4.6l-1.04-3.32ZM86.69,41.28h3.82l-1.92-6.16-1.9,6.16Z"
              fill="currentColor"
              strokeWidth="0"
            />
            <path
              d="M98.47,47.91v-17.77h5.73c1.57,0,2.98.36,4.24,1.07,1.25.71,2.23,1.72,2.94,3.01s1.06,2.75,1.07,4.36v.82c0,1.63-.34,3.09-1.03,4.38-.69,1.29-1.66,2.3-2.91,3.03-1.25.73-2.64,1.1-4.18,1.1h-5.85ZM102.75,33.44v11.17h1.49c1.23,0,2.17-.44,2.83-1.31.66-.87.99-2.17.99-3.9v-.77c0-1.72-.33-3.01-.99-3.88-.66-.87-1.62-1.31-2.88-1.31h-1.44Z"
              fill="currentColor"
              strokeWidth="0"
            />
            <path
              d="M124.47,44.59h-5.87l-1.03,3.32h-4.58l6.52-17.77h4.03l6.57,17.77h-4.6l-1.04-3.32ZM119.62,41.28h3.82l-1.92-6.16-1.9,6.16Z"
              fill="currentColor"
              strokeWidth="0"
            />
            <path
              d="M138.01,41.62h-2.32v6.29h-4.28v-17.77h7c2.11,0,3.75.47,4.94,1.4,1.19.93,1.78,2.26,1.78,3.96,0,1.24-.25,2.26-.75,3.07-.5.81-1.28,1.47-2.35,1.98l3.71,7.18v.18h-4.59l-3.14-6.29ZM135.69,38.33h2.71c.81,0,1.43-.21,1.84-.64.41-.43.62-1.03.62-1.79s-.21-1.37-.62-1.81c-.41-.44-1.03-.65-1.83-.65h-2.71v4.9Z"
              fill="currentColor"
              strokeWidth="0"
            />
          </svg>
        </div>
      )}
    </Link>
  )
}
